import type { OnInit, OnDestroy} from '@angular/core';
import { Component, inject, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { Subject, takeUntil, finalize, catchError, of, forkJoin } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import type { PageEvent } from '@angular/material/paginator';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import type { IUser } from '../../../shared/models/user.model';
import type { ActiveCycleData, PastCycleData } from '../../../shared/models/cycle-management.models';
import type { SubManager } from '../../../core/services/manager-dashboard.service';
import { ManagerDashboardService } from '../../../core/services/manager-dashboard.service';
import { SnackbarService } from '../../../core/services/snackbar.service';
import { CustomTooltipDirective } from '../../../shared/directives/custom-tooltip.directive';
import { DefaultImageDirective } from '../../../shared/directives/default-image.directive';
import { UserInitialsPipe } from '../../../shared/pipes/user-initials.pipe';
import { getProfileImageUrl } from '../../../shared/utils/profile-image.util';

/**
 * Sub-Manager Cycles Component
 *
 * Displays active and past cycles for a selected sub-manager with:
 * - Active cycles list with progress tracking
 * - Paginated past cycles history
 * - Collapsible sub-managers sidebar for navigation
 * - Cycle completion statistics
 *
 * This component allows system managers to monitor their sub-managers'
 * cycle assignments and view detailed cycle information.
 *
 * Data is pre-loaded via the subManagerCyclesResolver which validates
 * access and fetches manager details before component initialization.
 */
@Component({
  selector: 'app-sub-manager-cycles',
  standalone: true,
  imports: [
    CommonModule,
    NgOptimizedImage,
    RouterModule,
    MatIconModule,
    MatButtonModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    CustomTooltipDirective,
    UserInitialsPipe,
    DefaultImageDirective
  ],
  templateUrl: './sub-manager-cycles.component.html',
  styleUrls: ['./sub-manager-cycles.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubManagerCyclesComponent implements OnInit, OnDestroy {
  // Dependency injection
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly managerDashboardService = inject(ManagerDashboardService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  // Utility functions
  readonly getProfileImageUrl = getProfileImageUrl;

  // Manager details (populated by resolver)  // Manager details from route params
  managerPrid = '';
  managerName = '';
  managerId = 0;
  marketId: number | undefined;
  currentUser: IUser | null = null;

  // Component state
  isLoading = false;
  isLoadingPastCycles = false;
  isLoadingSubManagers = false;
  error: string | null = null;
  isSidebarCollapsed = false;

  // Data properties
  activeCycles: ActiveCycleData[] = [];
  pastCycles: PastCycleData[] = [];
  subManagers: SubManager[] = [];

  // Pagination
  totalPastCycles = 0;
  pageSize = 10;
  pageIndex = 0;
  pageSizeOptions = [5, 10, 25];

  ngOnInit(): void {
    // Get resolved data from router
    this.route.data.pipe(takeUntil(this.destroy$)).subscribe(data => {
      const resolvedData = data['resolvedData'];

      if (!resolvedData) {
        // Resolver failed, user should have been redirected
        return;
      }

      // Set component properties from resolved data
      this.currentUser = resolvedData.currentUser;
      this.marketId = resolvedData.marketId;
      this.managerId = resolvedData.manager.id;
      this.managerName = `${resolvedData.manager.first_name} ${resolvedData.manager.last_name}`;
      this.managerPrid = resolvedData.manager.prid;

      // Reset pagination when switching managers
      this.pageIndex = 0;

      // Load manager data (cycles and sub-managers)
      this.loadManagerData();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadManagerData(): void {
    if (!this.managerId || !this.marketId || !this.currentUser) {
      return;
    }

    this.isLoading = true;
    this.error = null;

    // Use actual manager user object from resolved data
    const managerUser: IUser = {
      ...this.currentUser,
      id: this.managerId,
      prid: this.managerPrid,
      first_name: this.managerName.split(' ')[0] || '',
      last_name: this.managerName.split(' ').slice(1).join(' ') || ''
    };

    forkJoin({
      activeCycles: this.managerDashboardService.getActiveCycles(managerUser, this.marketId).pipe(
        catchError(error => {
          console.error('Error loading active cycles:', error);
          return of([]);
        })
      ),
      paginatedPastCycles: this.managerDashboardService.getPaginatedPastCycles(
        this.managerId,
        this.pageSize,
        this.pageIndex,
        this.marketId
      ).pipe(
        catchError(error => {
          console.error('Error loading past cycles:', error);
          return of({ cycles: [], totalCount: 0, pageSize: this.pageSize, pageNumber: this.pageIndex, totalPages: 0 });
        })
      )
    })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (result) => {
          this.activeCycles = result.activeCycles;
          this.pastCycles = result.paginatedPastCycles.cycles;
          this.totalPastCycles = result.paginatedPastCycles.totalCount;

          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading manager data:', error);
          this.error = 'Failed to load manager data. Please try again.';
          this.snackbarService.showError('Failed to load manager data');
        }
      });

    // Load sub-managers independently (non-blocking, skip global loader)
    this.isLoadingSubManagers = true;
    this.managerDashboardService.getSubManagers(this.currentUser.prid, this.marketId, true)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isLoadingSubManagers = false;
          this.cdr.markForCheck();
        }),
        catchError(error => {
          console.error('Error loading sub-managers:', error);
          return of([]);
        })
      )
      .subscribe({
        next: (subManagers) => {
          // Sub-managers with profile images (including the currently viewed manager)
          // Only show managers registered in the system
          // Exclude the logged-in user from the list
          this.subManagers = subManagers
            .filter(manager => !this.currentUser || manager.prid.toLowerCase() !== this.currentUser.prid.toLowerCase()) // Exclude logged-in user
            .filter(manager => manager.is_system_manager)
            .map(manager => ({
              ...manager,
              profileUrl: getProfileImageUrl(manager.prid)
            }));

          this.cdr.markForCheck();
        }
      });
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadManagerData();
  }

  onViewCycle(cycleId: number): void {
    this.router.navigate(['/sub-manager-cycle-forms'], {
      queryParams: {
        cycleId,
        managerPrid: this.managerPrid
      }
    });
  }

  onViewSubManager(manager: SubManager): void {
    // Don't navigate if clicking on the currently viewed manager
    if (manager.prid.toLowerCase() === this.managerPrid.toLowerCase()) {
      return;
    }

    // Navigate to another manager view
    this.router.navigate(['/sub-manager-cycles'], {
      queryParams: {
        prid: manager.prid
      }
    });
  }

  onBackToDashboard(): void {
    this.router.navigate(['/dashboard']);
  }

  toggleSidebar(): void {
    this.isSidebarCollapsed = !this.isSidebarCollapsed;
    this.cdr.markForCheck();
  }

  refreshData(): void {
    this.loadManagerData();
  }
}
